import { Helmet } from "react-helmet-async";
import { NavLink } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { buildBreadcrumbSchema, type Crumb } from "../utils/structuredData";

/** A crumb with `onClick` moves between steps of the same page (the catalog) instead of following its path. */
export interface BreadcrumbItem extends Crumb {
  onClick?: () => void;
}

interface BreadcrumbsProps {
  items: BreadcrumbItem[];
  /** The real pages behind the trail, for structured data; defaults to `items`. */
  schemaItems?: Crumb[];
}

// Visible, accessible breadcrumb trail that also emits a matching BreadcrumbList
// JSON-LD graph. Rendering both from the same `items` array keeps the structured
// data consistent with what the user sees, which is what Google expects.
const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ items, schemaItems = items }) => {
  if (items.length < 2) {
    return null;
  }

  const schema = buildBreadcrumbSchema(schemaItems.map(({ name, path }) => ({ name, path })));

  return (
    <nav aria-label="Breadcrumb" className="mb-6 sm:mb-8">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>
      <ol className="flex flex-wrap items-center gap-1.5 text-sm text-text-muted">
        {items.map((item, index) => {
          const isLast = index === items.length - 1;

          return (
            <li key={`${index}-${item.name}`} className="flex items-center gap-1.5">
              {isLast ? (
                <span className="font-medium text-text-secondary" aria-current="page">
                  {item.name}
                </span>
              ) : item.onClick ? (
                <button type="button" onClick={item.onClick} className="hover:text-primary transition-colors duration-200">
                  {item.name}
                </button>
              ) : (
                <NavLink to={item.path} className="hover:text-primary transition-colors duration-200">
                  {item.name}
                </NavLink>
              )}
              {!isLast && <ChevronRight className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" />}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default Breadcrumbs;
